import { DecisionTreeContent } from "@amzn/ask-legal-domain";
import { useEffect, useState, useRef, useCallback } from "react";
import { useEdgesState, useNodesState } from "reactflow";
import { CustomNode } from "../components/decision-tree-container/custom-resources/CustomNode";
import { CustomEdge } from "../components/decision-tree-container/custom-resources/CustomEdge";
import { getReactFlowEdge, getReactFlowNode } from "../factory/decision-tree-factory";
import { calculateLayout, calculateOrphanPositions, Position } from "../utils/decisiontree/elk-tree-layout";
import { buildGraph } from "../utils/decisiontree/graph-builder";

/**
 * Compute a structure fingerprint that captures ONLY what affects layout:
 * - Which nodes exist (IDs)
 * - Which edges exist (parent→child relationships)
 * - Layout mode (customized vs auto)
 *
 * Changes to overviewText, question content, etc. do NOT change this hash,
 * so they skip ELK entirely.
 */
function getStructureHash(content: DecisionTreeContent): string {
    const nodeIds = content.allNodes.map(n => n.id).sort().join(",");
    const edges = content.allNodes
        .flatMap(n => (n.children || []).map(c => `${n.id}->${c.nodeId}`))
        .sort()
        .join(",");
    return `${content.rootNodeId}|${nodeIds}|${edges}|${content.useCustomizedLayout ?? false}`;
}

/**
 * Decision Tree Layout Manager
 *
 * Uses ELK (Eclipse Layout Kernel) for industrial-grade graph layout.
 * Uses buildGraph() for unified, single-pass node classification and edge computation.
 *
 * Performance optimization: Structure-Aware Layout Guard
 * - Only runs ELK when graph structure changes (nodes added/removed, edges changed)
 * - Text edits, position saves, etc. skip ELK entirely — only update node data
 * - Typical decision tree editing (text changes after reload) goes from ~50ms ELK → <1ms
 */
export const useLayoutManager = (props: {
    content: DecisionTreeContent;
}) => {
    const contentRef = useRef<DecisionTreeContent>(props.content);

    const [nodes, setNodes, onNodesChange] = useNodesState<CustomNode.Data>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState<CustomEdge.Data>([]);

    const [useCustomizedLayout, setUseCustomizedLayout] = useState<boolean>(
        props.content.useCustomizedLayout ?? false
    );

    const [layoutTick, setLayoutTick] = useState(0);
    const isRepositioningRef = useRef(false);
    const needsRelayoutRef = useRef(false);

    // Structure-aware layout cache
    const lastStructureHash = useRef<string>("");
    const lastPositions = useRef<Map<string, Position>>(new Map());

    useEffect(() => {
        if (layoutTick === 0) return;
        repositionWithData(contentRef.current);
    }, [layoutTick]);

    useEffect(() => {
        repositionWithData(contentRef.current);
    }, []);

    const update = useCallback((newContent: DecisionTreeContent) => {
        contentRef.current = newContent;
        setUseCustomizedLayout(newContent.useCustomizedLayout ?? false);
        setLayoutTick(prev => prev + 1);
    }, []);

    /**
     * Build ReactFlow nodes + edges from content and positions.
     * Uses buildGraph() for single-pass node classification and edge computation.
     *
     * Replaces the previous multi-BFS approach:
     * - Old: 4 BFS passes + O(n²) edge lookup + separate orphan edge loop
     * - New: 1 BFS in buildGraph() + O(n) edge iteration + unified classification
     */
    const buildNodesAndEdges = (
        content: DecisionTreeContent,
        positions: Map<string, Position>
    ) => {
        // Single-pass graph analysis: classify all nodes and edges
        const graph = buildGraph(content);

        if (graph.connectedCount === 0 && graph.orphanCount === 0) {
            return { nodes: [] as CustomNode.Node[], edges: [] as CustomEdge.Edge[] };
        }

        const finalPositions = new Map(positions);

        // Customized Layout: override with user-dragged positions
        if (content.useCustomizedLayout) {
            for (const [, node] of graph.nodeMap) {
                if (node.position) {
                    finalPositions.set(node.id, node.position);
                }
            }
        }

        // Calculate orphan positions for nodes not in ELK layout
        let mainTreeMaxY = 0;
        finalPositions.forEach(pos => {
            if (pos.y > mainTreeMaxY) mainTreeMaxY = pos.y;
        });

        const orphanNodes = graph.nodes
            .filter(cn => cn.classification !== "connected")
            .map(cn => cn.node);

        const orphanPositions = orphanNodes.length > 0
            ? calculateOrphanPositions(orphanNodes, mainTreeMaxY)
            : new Map<string, Position>();

        // Build ReactFlow nodes from classified nodes
        const _nodes: CustomNode.Node[] = graph.nodes.map(classifiedNode => {
            const { node, classification } = classifiedNode;
            const isOrphan = classification !== "connected";

            const position = finalPositions.get(node.id)
                || orphanPositions.get(node.id)
                || { x: 0, y: mainTreeMaxY + 300 };

            return getReactFlowNode(
                node,
                position,
                content.useCustomizedLayout ?? false,
                isOrphan,
                node.id === content.rootNodeId  // Explicit root check instead of deprecated parentNodeId
            );
        });

        // Build ReactFlow edges from classified edges — single pass, no BFS
        const _edges: CustomEdge.Edge[] = graph.edges.map(classifiedEdge => {
            const edge = getReactFlowEdge(
                classifiedEdge.parentId,
                classifiedEdge.childId,
                classifiedEdge.answer,
                classifiedEdge.childIndex,
                classifiedEdge.totalChildren,
                classifiedEdge.classification
            );
            return {
                ...edge,
                id: `${edge.source}:${edge.sourceHandle}->${edge.target}:${edge.targetHandle}`
            };
        });

        // Deduplicate edges by ID (handles DAG converging paths)
        const edgeMap = new Map<string, CustomEdge.Edge>();
        _edges.forEach(edge => edgeMap.set(edge.id, edge));

        return { nodes: _nodes, edges: Array.from(edgeMap.values()) };
    };

    /**
     * Core reposition logic with structure-aware optimization.
     *
     * If graph structure hasn't changed (same nodes + edges), skips ELK entirely
     * and only updates node data (text, styles). This makes text edits + reloads
     * nearly instant (<1ms vs ~50ms for ELK).
     */
    const repositionWithData = async (content: DecisionTreeContent) => {
        if (isRepositioningRef.current) {
            needsRelayoutRef.current = true;
            return;
        }

        if (!content.allNodes || content.allNodes.length === 0) return;
        if (!content.allNodes.some(n => n.id === content.rootNodeId)) return;

        const currentHash = getStructureHash(content);

        // Structure unchanged — skip ELK, just update node data + edges
        if (currentHash === lastStructureHash.current && lastPositions.current.size > 0) {
            const { nodes: _nodes, edges: _edges } = buildNodesAndEdges(content, lastPositions.current);
            setNodes(_nodes);
            setEdges(_edges);
            return;
        }

        // Structure changed — full ELK layout
        isRepositioningRef.current = true;

        try {
            const positions = await calculateLayout(content);

            // Cache for next time
            lastPositions.current = positions;
            lastStructureHash.current = currentHash;

            const { nodes: _nodes, edges: _edges } = buildNodesAndEdges(content, positions);
            setNodes(_nodes);
            setEdges(_edges);
        } finally {
            isRepositioningRef.current = false;

            if (needsRelayoutRef.current) {
                needsRelayoutRef.current = false;
                setLayoutTick(prev => prev + 1);
            }
        }
    };

    return {
        nodes,
        edges,
        setNodes,
        setEdges,
        onNodesChange,
        onEdgesChange,
        contentRef,
        update,
        useCustomizedLayout
    };
};
